import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Bookmark,
} from '@mui/icons-material';
import BookMyOpAPI from '../../services/BookMyOpapi';
import DoctorCard from '../../components/DoctorCard';
import HospitalCard from '../../components/HospitalCard';
import { useBookMyOpAuth } from '../../contexts/BookMyOpAuthContext';

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 2),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(4, 3),
  },
  background: theme.palette.background.default,
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  overflowX: 'auto',
  overflowY: 'visible',
  scrollBehavior: 'smooth',
  paddingBottom: theme.spacing(1),
  paddingX: theme.spacing(0.5),
  '&::-webkit-scrollbar': {
    height: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.primary.main, 0.1),
    borderRadius: 2,
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.3),
    borderRadius: 2,
  },
  '@media (max-width: 600px)': {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
}));

const ScrollButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    transform: 'translateY(-50%) scale(1.1)',
  },
  '&:disabled': {
    opacity: 0.3,
  },
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  background: 'white',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    transform: 'translateY(-2px)',
    color: 'white',
    border: 'none',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  transition: 'all 0.3s ease',
}));

const StyledToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: theme.spacing(0.75, 2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      color: 'white',
      border: 'none',
      '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
      },
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
  },
}));

const HomeFavourites = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useBookMyOpAuth();
  
  const [viewType, setViewType] = useState('doctors');
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const fetchingRef = useRef(false);
  const hasFetchedDoctors = useRef(false);
  const hasFetchedHospitals = useRef(false);

  // Extract doctors from API response
  const extractDoctors = useCallback((doctorsData) => {
    let doctors = [];
    if (Array.isArray(doctorsData)) {
      doctors = doctorsData;
    } else if (doctorsData.favorites && Array.isArray(doctorsData.favorites)) {
      doctors = doctorsData.favorites;
    } else if (doctorsData.results && Array.isArray(doctorsData.results)) {
      doctors = doctorsData.results;
    } else if (doctorsData.data) {
      if (Array.isArray(doctorsData.data)) {
        doctors = doctorsData.data;
      } else if (doctorsData.data.favorites && Array.isArray(doctorsData.data.favorites)) {
        doctors = doctorsData.data.favorites;
      } else if (doctorsData.data.results && Array.isArray(doctorsData.data.results)) {
        doctors = doctorsData.data.results;
      }
    }
    
    return doctors
      .filter(item => item.favorite_type === 'doctor' && item.doctor)
      .map(item => item.doctor)
      .filter(doc => doc && doc.id)
      .slice(0, 10); // Limit to 10
  }, []);

  // Extract hospitals from API response
  const extractHospitals = useCallback((hospitalsData) => {
    let hospitals = [];
    if (Array.isArray(hospitalsData)) {
      hospitals = hospitalsData;
    } else if (hospitalsData.favorites && Array.isArray(hospitalsData.favorites)) {
      hospitals = hospitalsData.favorites;
    } else if (hospitalsData.results && Array.isArray(hospitalsData.results)) {
      hospitals = hospitalsData.results;
    } else if (hospitalsData.data) {
      if (Array.isArray(hospitalsData.data)) {
        hospitals = hospitalsData.data;
      } else if (hospitalsData.data.favorites && Array.isArray(hospitalsData.data.favorites)) {
        hospitals = hospitalsData.data.favorites;
      } else if (hospitalsData.data.results && Array.isArray(hospitalsData.data.results)) {
        hospitals = hospitalsData.data.results;
      }
    }
    
    return hospitals
      .filter(item => item.favorite_type === 'service_provider' && item.service_provider)
      .map(item => item.service_provider)
      .filter(hosp => hosp && hosp.id)
      .slice(0, 10); // Limit to 10
  }, []);

  // Fetch doctors
  const fetchDoctors = useCallback(async () => {
    if (hasFetchedDoctors.current || fetchingRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      const response = await BookMyOpAPI.getFavoriteDoctors();
      const extractedDoctors = extractDoctors(response);
      setDoctors(extractedDoctors);
      hasFetchedDoctors.current = true;
    } catch (err) {
      console.error('Error fetching favorite doctors:', err);
      setError('Failed to load favorite doctors');
      setDoctors([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [extractDoctors]);

  // Fetch hospitals
  const fetchHospitals = useCallback(async () => {
    if (hasFetchedHospitals.current || fetchingRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      const response = await BookMyOpAPI.getFavoriteProviders();
      const extractedHospitals = extractHospitals(response);
      setHospitals(extractedHospitals);
      hasFetchedHospitals.current = true;
    } catch (err) {
      console.error('Error fetching favorite hospitals:', err);
      setError('Failed to load favorite hospitals');
      setHospitals([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [extractHospitals]);

  // Fetch both doctors and hospitals on mount to check if there's any data
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Fetch both on initial load
    if (!hasFetchedDoctors.current) {
      fetchDoctors();
    }
    if (!hasFetchedHospitals.current) {
      fetchHospitals();
    }
  }, [isAuthenticated, fetchDoctors, fetchHospitals]);

  // Fetch data when view type changes (if not already fetched)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (viewType === 'doctors' && !hasFetchedDoctors.current) {
      fetchDoctors();
    } else if (viewType === 'hospitals' && !hasFetchedHospitals.current) {
      fetchHospitals();
    }
  }, [viewType, isAuthenticated, fetchDoctors, fetchHospitals]);

  // Listen for favorites updates
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      // Reset fetch flags to allow refresh
      hasFetchedDoctors.current = false;
      hasFetchedHospitals.current = false;
      
      // Refetch current view
      if (viewType === 'doctors') {
        fetchDoctors();
      } else {
        fetchHospitals();
      }
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    return () => window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
  }, [viewType, fetchDoctors, fetchHospitals]);

  const handleViewTypeChange = (event, newViewType) => {
    if (newViewType !== null) {
      setViewType(newViewType);
    }
  };

  const handleViewAll = () => {
    if (isMobile) {
      navigate('/find-doctors/saved');
    } else {
      navigate('/find-doctors?list=saved');
    }
  };

  const updateScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      // Update scroll buttons after scroll
      setTimeout(updateScrollButtons, 100);
    }
  };

  // Update scroll buttons when items change or container resizes
  useEffect(() => {
    updateScrollButtons();
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [doctors, hospitals, viewType, updateScrollButtons]);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const currentItems = viewType === 'doctors' ? doctors : hospitals;
  const hasItems = currentItems.length > 0;
  const hasDoctors = doctors.length > 0;
  const hasHospitals = hospitals.length > 0;
  const hasAnyData = hasDoctors || hasHospitals;
  
  // Check if both have been fetched (not loading) and both are empty
  // Only hide if both have been fetched, not currently loading, and no data exists
  const bothFetched = hasFetchedDoctors.current && hasFetchedHospitals.current;
  const isStillFetching = fetchingRef.current || loading;
  const shouldHideSection = bothFetched && !isStillFetching && !hasAnyData;

  // Hide section completely if no data exists
  if (shouldHideSection) {
    return null;
  }

  return (
    <StyledContainer>
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Bookmark sx={{ color: theme.palette.primary.main, fontSize: '1.5rem' }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                color: 'text.primary',
              }}
            >
              Your Favourite {viewType === 'doctors' ? 'Doctors' : 'Hospitals'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Toggle Switch */}
            <StyledToggleGroup
              value={viewType}
              exclusive
              onChange={handleViewTypeChange}
              size="small"
            >
              <ToggleButton value="doctors">Doctors</ToggleButton>
              <ToggleButton value="hospitals">Hospitals</ToggleButton>
            </StyledToggleGroup>

            {/* View All Button */}
            {hasItems && (
              <ViewAllButton
                variant="outlined"
                onClick={handleViewAll}
                endIcon={<KeyboardArrowRight />}
              >
                View All
              </ViewAllButton>
            )}
          </Box>
        </Box>

        {/* Content */}
        {loading && !hasItems ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={50} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : !hasItems ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 3,
              px: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Add hospitals and doctors to your favourites to see them here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Left Scroll Button */}
            {canScrollLeft && (
              <ScrollButton
                onClick={() => handleScroll('left')}
                sx={{ left: { xs: 8, md: 16 } }}
                size="small"
              >
                <KeyboardArrowLeft />
              </ScrollButton>
            )}

            {/* Scroll Container */}
            <ScrollContainer ref={scrollContainerRef}>
              {viewType === 'doctors' ? (
                doctors.map((doctor, index) => (
                  <Box
                    key={doctor.id}
                    sx={{
                      minWidth: { xs: 280, sm: 320, md: 360 },
                      maxWidth: { xs: 280, sm: 320, md: 360 },
                    }}
                  >
                    <DoctorCard
                      doctor={doctor}
                      index={index}
                      onClick={(doctorId) => navigate(`/doctors/${doctorId}`)}
                    />
                  </Box>
                ))
              ) : (
                hospitals.map((hospital, index) => (
                  <Box
                    key={hospital.id}
                    sx={{
                      minWidth: { xs: 280, sm: 320, md: 360 },
                      maxWidth: { xs: 280, sm: 320, md: 360 },
                    }}
                  >
                    <HospitalCard
                      hospital={hospital}
                      index={index}
                      onViewDoctors={(hospitalId) =>
                        navigate(`/find-doctors?provider=${hospitalId}`)
                      }
                    />
                  </Box>
                ))
              )}
            </ScrollContainer>

            {/* Right Scroll Button */}
            {canScrollRight && (
              <ScrollButton
                onClick={() => handleScroll('right')}
                sx={{ right: { xs: 8, md: 16 } }}
                size="small"
              >
                <KeyboardArrowRight />
              </ScrollButton>
            )}
          </Box>
        )}
      </Container>
    </StyledContainer>
  );
};

export default HomeFavourites;

